/*
 * IMPORTS
 */
import React from "react"; // Npm: react.js library.
import Moment from "moment"; // Npm: moment.js library.
import _ from "underscore"; // Npm: underscore.js library.
import { toast } from "react-hot-toast"; // Npm: React hot toast.
import { useMutation, useQuery } from "@apollo/client"; // Npm: Apollo client.
import { IoMdAdd } from "react-icons/io"; // Npm: React icons.
import { HiArrowPath } from "react-icons/hi2"; // Npm: React icons.
import { DataGrid } from "@mui/x-data-grid"; // Npm: Material UI Data Grid.
import {
  Box,
  Button,
  Flex,
  Text,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react"; // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import DownloadToExcel from "components/DownloadToExcel";
import TableSpinner from "components/TableSpinner";
import Modal from "components/Modal";
import CustomerNotify from "components/CustomerNotify";
import CustomerChangeTps from "components/CustomerChangeTps";
import CustomerBlockBetweenDuration from "components/CustomerBlockBetweenDuration";
import CustomerUpsert from "components/CustomerUpsert";
import CustomerDelete from "components/CustomerDelete";
import AttachRouteAndRate from "components/AttachRouteAndRate";
import AttachAccountManager from "components/AttachAccountManager";
import CreditManagementUpsert from "components/CustomerCreditManagementUpsert";
import CustomerPasswordChange from "components/CustomerPasswordChange";
import CustomerDropDownOptions from "components/CustomerDropDownOptions";
import WarningAudio from "components/WarningAudio";
import { MemoizedInput, MemoizedSelect } from "components/MemoizedInput";

/*
 * GRAPHS
 */
import CustomerReadQuery from "./__query__/index.customer.read.query";
import CustomerRateEmailMutation from "./__mutation__/index.rate.email.mutation";

/*
 * OBJECTS
 */
const Index = () => {
  // Const assignment.
  const _successFlags = React.useMemo(
    () =>
      Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(
        (i) => i.key,
      ),
    [],
  );

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
    id: false,
  });
  const [searchQuery, setSearchQuery] = React.useState("");
  const [filterModel, setFilterModel] = React.useState({
    items: [],
    logicOperator: "and",
    quickFilterValues: [],
    quickFilterLogicOperator: "and",
  });
  const _setFilterDebounced = React.useMemo(
    () => _.debounce(setFilterModel, 500),
    [],
  );
  const [customerType, setCustomerType] = React.useState(void 0);
  const [customerRateEmailInProgress, setCustomerRateEmailInProgress] =
    React.useState([]);

  const [MutationRateEmail] = useMutation(CustomerRateEmailMutation);

  const {
    onOpen: onCustomerBlockDurationOpen,
    isOpen: isCustomerBlockDurationOpen,
    onClose: onCustomerBlockDurationClose,
  } = useDisclosure();
  const {
    onOpen: onCustomerCreateOpen,
    isOpen: isCustomerCreateOpen,
    onClose: onCustomerCreateClose,
  } = useDisclosure();
  const {
    onOpen: onCustomerUpdateOpen,
    isOpen: isCustomerUpdateOpen,
    onClose: onCustomerUpdateClose,
  } = useDisclosure();
  const {
    onOpen: onCustomerDeleteOpen,
    isOpen: isCustomerDeleteOpen,
    onClose: onCustomerDeleteClose,
  } = useDisclosure();
  const {
    onOpen: onCustomerNotifyOpen,
    isOpen: isCustomerNotifyOpen,
    onClose: onCustomerNotifyClose,
  } = useDisclosure();
  const {
    onOpen: onCustomerChangeTpsOpen,
    isOpen: isCustomerChangeTpsOpen,
    onClose: onCustomerChangeTpsClose,
  } = useDisclosure();
  const {
    onOpen: onCustomerAttachAccountManagerOpen,
    isOpen: isCustomerAttachAccountManagerOpen,
    onClose: onCustomerAttachAccountManagerClose,
  } = useDisclosure();
  const {
    onOpen: onCreditManagementUpsertOpen,
    isOpen: isCreditManagementUpsertOpen,
    onClose: onCreditManagementUpsertClose,
  } = useDisclosure();
  const {
    onOpen: onCustomerRatePlanAndRoutePlanAttachFormOpen,
    isOpen: isCustomerRatePlanAndRoutePlanAttachFormOpen,
    onClose: onCustomerRatePlanAndRoutePlanAttachFormClose,
  } = useDisclosure();
  const {
    onOpen: onCustomerPasswordChangeOpen,
    isOpen: isCustomerPasswordChangeOpen,
    onClose: onCustomerPasswordChangeClose,
  } = useDisclosure();

  const _isPreviousPageRef = React.useRef(false);
  const _paginationModelRef = React.useRef(paginationModel);
  const _rowCountRef = React.useRef(0);
  const _creditAlertedCustomerIdsRef = React.useRef(new Set());
  const _warningAudioRef = React.useRef(null);

  const _ResetPagination = React.useCallback(() => {
    _isPreviousPageRef.current = false;
    setPaginationModel((current) => {
      const nextPaginationModel = { ...current, page: 0 };

      _paginationModelRef.current = nextPaginationModel;

      return nextPaginationModel;
    });
  }, []);

  const _onSearchInputChange = React.useRef(
    _.debounce((e) => {
      _ResetPagination();
      setSearchQuery(e.target.value);
    }, 1000),
  ).current;

  React.useEffect(() => {
    return () => {
      _setFilterDebounced.cancel?.();
      _onSearchInputChange.cancel?.();
    };
  }, [_setFilterDebounced]);

  // Data assignment.
  const _isFilterApplied = filterModel?.items?.some(
    (i) => i.value != void 0 && i.value !== "",
  );
  const _appliedFilter = _isFilterApplied ? filterModel : void 0;

  const _QueryCustomerReadVariables = React.useMemo(() => ({
      customerType,
      skip: paginationModel.pageSize * paginationModel.page,
      take: paginationModel.pageSize,
      search: searchQuery,
      filter: _appliedFilter,
    }), [_appliedFilter, customerType, paginationModel.page, paginationModel.pageSize, searchQuery]);

  const _QueryCustomerRead = useQuery(CustomerReadQuery, {
    variables: _QueryCustomerReadVariables,
    fetchPolicy: _isPreviousPageRef.current
      ? "cache-first"
      : Object.React.App.fetchPolicy,
    pollInterval: Object.React.App.pollInterval,
  });

  const { refetch } = _QueryCustomerRead;

  const _isFirstLoadCompleted = React.useRef(false);
  const _tableHeaderHeightRef = React.useRef(0);
  const _isCurrentViewMobile =
    useBreakpointValue({ base: true, md: false }, { fallback: "md" }) ?? false;

  // Data assignment.
  if (
    !_QueryCustomerRead.loading &&
    0 < _QueryCustomerRead.data?.CustomerRead?.length
  )
    _isFirstLoadCompleted.current = true;
  if (
    0 === _QueryCustomerRead?.data?.CustomerRead?.length ||
    (0 < _QueryCustomerRead?.data?.CustomerRead?.length &&
      !_.every(_.pluck(_QueryCustomerRead?.data?.CustomerRead, "status"), (j) =>
        _successFlags.includes(j),
      ))
  )
    _isFirstLoadCompleted.current = false;

  // Event handler.
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const _HandleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);

    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  }, [refetch]);
  const _HandlePaginationModelChange = React.useCallback((__paginationModel) => {
    // Only backward pagination with the same page size should prefer cached rows.
    _isPreviousPageRef.current =
      __paginationModel.page < _paginationModelRef.current.page &&
      __paginationModel.pageSize === _paginationModelRef.current.pageSize;

    _paginationModelRef.current = __paginationModel;
    setPaginationModel(__paginationModel);
  }, []);
  const _HandleFilterModelChange = React.useCallback((__filterModel) => {
    _ResetPagination();
    _setFilterDebounced(__filterModel);
  }, [_ResetPagination, _setFilterDebounced]);
  const _HandleCustomerTypeChange = React.useCallback((e) => {
    _ResetPagination();
    setCustomerType(
      _.isEmpty(e?.target?.value) ? void 0 : e?.target?.value,
    );
  }, [_ResetPagination]);

  React.useEffect(() => {
    if (_QueryCustomerRead.loading) return;

    const _creditAlertCustomers = _.compact(
      _QueryCustomerRead?.data?.CustomerRead?.map((item) => {
        const _balance = Number(item?.Credit?.balance);
        const _alertAmount = Number(item?.Credit?.alertAmount);

        if (
          !_successFlags.includes(item?.status) ||
          !Number.isFinite(_balance) ||
          !Number.isFinite(_alertAmount)
        )
          return null;

        if (_balance > _alertAmount) {
          _creditAlertedCustomerIdsRef.current.delete(item?.id);

          return null;
        }

        if (_creditAlertedCustomerIdsRef.current.has(item?.id)) return null;

        return item;
      }),
    );

    if (_.isEmpty(_creditAlertCustomers)) return;

    _creditAlertCustomers.forEach((item) =>
      _creditAlertedCustomerIdsRef.current.add(item?.id),
    );
    toast(
      `Low balance warning: ${_creditAlertCustomers
        .slice(0, 3)
        .map(
          (item) => item?.Company?.displayName || item?.displayName || item?.id,
        )
        .join(
          ", ",
        )}${3 < _creditAlertCustomers.length ? ` +${_creditAlertCustomers.length - 3} more` : ""}`,
    );
    _warningAudioRef.current?.play?.();
  }, [
    _QueryCustomerRead.loading,
    _QueryCustomerRead?.data?.CustomerRead,
    _successFlags,
  ]);

  // Columns definition
  const columns = React.useMemo(
    () => [
      {
        field: "sno",
        headerName: "S No.",
        align: "center",
        headerAlign: "center",
        width: 80,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
      },
      {
        field: "id",
        headerName: "ID",
        align: "center",
        editable: false,
        sortable: false,
        filterable: false,
        headerAlign: "center",
      },
      {
        field: "displayName",
        headerName: "Company Name",
        description: "Name of company.",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 250,
      },
      {
        field: "Company.referenceNumber",
        headerName: "Ref.Number",
        description: "Reference number.",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "type",
        headerName: "Type",
        description: "Customer type.",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        width: 180,
      },
      {
        field: "Company.email",
        headerName: "Company Email",
        description: "Company email address.",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 250,
      },
      {
        field: "phone",
        headerName: "Phone",
        description: "Phone number.",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        width: 200,
      },
      {
        field: "Billing.currency",
        headerName: "Currency",
        description: "Billing currency.",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        width: 150,
      },
      {
        field: "Credit.balance",
        headerName: "Balance",
        description: "Current balance.",
        editable: false,
        sortable: true,
        filterable: true,
        type: "number",
        align: "center",
        headerAlign: "center",
        width: 150,
      },
      {
        field: "Credit.alertAmount",
        headerName: "Alert Amount",
        description: "Credit alert amount.",
        editable: false,
        sortable: true,
        filterable: true,
        type: "number",
        align: "center",
        headerAlign: "center",
        width: 150,
      },
      {
        field: "overDraftLimit",
        headerName: "Over Draft Limit Amount",
        description: "Over draft limit.",
        editable: false,
        sortable: true,
        type: "number",
        filterable: true,
        align: "center",
        headerAlign: "center",
        width: 180,
      },
      {
        field: "AccountManager.displayName",
        headerName: "Account Manager",
        description: "Assigned account manager.",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 180,
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
          >
            {params.row.AccountManager ? (
              <Text
                borderRadius="10px"
                padding="5px 15px"
                lineHeight="18px"
                color="rgba(117, 81, 255, 1)"
                bg="rgba(117, 81, 255, .1)"
              >
                {params.row.AccountManager.displayName ?? "-"}
              </Text>
            ) : (
              <Text
                borderRadius="10px"
                padding="5px 15px"
                lineHeight="18px"
                color="rgba(238, 93, 80, 1)"
                bg="rgba(255, 242, 242, 1)"
              >
                Not Assigned
              </Text>
            )}
          </Box>
        ),
      },
      {
        field: "emailRates",
        headerName: "Email Rates",
        description: "Send rates via email.",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        width: 150,
        renderCell: ({ row }) => (
          <Button
            fontSize="15px"
            fontWeight={500}
            py="3px"
            minH="unset"
            h="unset"
            width="max-content"
            margin="auto"
            borderRadius="10px"
            bg={
              customerRateEmailInProgress?.includes(row.originalId)
                ? "purple.100"
                : "purple.50"
            }
            _focus={{ bg: "purple.100" }}
            _active={{ bg: "purple.100" }}
            _hover={{ bg: "purple.100" }}
            color="purple.600"
            onClick={() => {
              // Add given customer to progress.
              setCustomerRateEmailInProgress((r) => [...r, row.originalId]);

              // Send email to given customer.
              MutationRateEmail({
                variables: {
                  customerAccountIds: _.compact(
                    _.flatten(_.pluck(row.CustomerAccount, "id")),
                  ),
                },
              })
                ?.then((r) => toast(r?.data?.RatePlanEmail?.message))
                ?.catch((e) => toast(e?.message ?? "Something went wrong"))
                ?.finally(() => {
                  // Remove given customer from progress.
                  setCustomerRateEmailInProgress((r) =>
                    _.without(r, row.originalId),
                  );
                });
            }}
          >
            Send Rates
          </Button>
        ),
      },
      {
        field: "createdAt",
        headerName: "CreatedAt",
        description: "Creation date.",
        editable: false,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        width: 180,
      },
      {
        field: "updatedAt",
        headerName: "UpdatedAt",
        description: "Last update date.",
        editable: false,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        width: 180,
      },
      {
        field: "action",
        headerName: "Actions",
        description: "Actions available.",
        editable: false,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        width: 100,
        renderCell: ({ row }) => (
          <CustomerDropDownOptions
            customerId={row.originalId}
            type={row.type}
            currency={row["Billing.currency"]}
            onCustomerNotify={onCustomerNotifyOpen}
            onCustomerUpdate={onCustomerUpdateOpen}
            onCustomerDelete={onCustomerDeleteOpen}
            onCustomerBlockDuration={onCustomerBlockDurationOpen}
            onCustomerChangeTps={onCustomerChangeTpsOpen}
            onCustomerPasswordChange={onCustomerPasswordChangeOpen}
            onRateAndRoutePlanAttach={
              onCustomerRatePlanAndRoutePlanAttachFormOpen
            }
            onAttachAccountManager={onCustomerAttachAccountManagerOpen}
            onCreditManagementUpsert={onCreditManagementUpsertOpen}
          />
        ),
      },
    ],
    [customerRateEmailInProgress],
  );

  // Rows data
  const rows = React.useMemo(
    () =>
      _.compact(
        _QueryCustomerRead?.data?.CustomerRead?.map((item, index) =>
          _successFlags.includes(item.status)
            ? {
                id:
                  item?.id ??
                  `customer-${paginationModel.page}-${paginationModel.pageSize}-${index}`,
                originalId: item?.id,
                sno: `${index + paginationModel.page * paginationModel.pageSize + 1}.`,
                displayName: item?.Company?.displayName || "-",
                "Company.referenceNumber":
                  item?.Company?.referenceNumber || "-",
                type: item?.type || "-",
                "Company.email": item?.Company?.email || "-",
                phone: item?.phone || "-",
                "Billing.currency": item?.Billing?.currency || "-",
                "Credit.balance": item?.Credit?.balance?.toFixed?.(5) ?? "0",
                "Credit.alertAmount":
                  item?.Credit?.alertAmount?.toFixed?.(5) ?? "-",
                overDraftLimit: item?.overDraftLimit?.toFixed?.(5) ?? "-",
                AccountManager: item?.AccountManager,
                CustomerAccount: item?.CustomerAccount,
                createdAt: item?.createdAt
                  ? new Date(item.createdAt).toISOString().split("T")[0]
                  : "-",
                updatedAt: item?.updatedAt
                  ? new Date(item.updatedAt).toISOString().split("T")[0]
                  : "-",
              }
            : null,
        ),
      ),
    [
      _QueryCustomerRead?.data?.CustomerRead,
      paginationModel.page,
      paginationModel.pageSize,
    ],
  );
  const _QueryCustomerReadRowCount = _.first(_QueryCustomerRead?.data?.CustomerRead)?._totalCount;
  const rowCount = React.useMemo(() => {
    if (undefined !== _QueryCustomerReadRowCount && null !== _QueryCustomerReadRowCount)
      _rowCountRef.current = _QueryCustomerReadRowCount;

    return _rowCountRef.current;
  }, [_QueryCustomerReadRowCount]);

  // Return component.
  return (
    <>
      <WarningAudio ref={_warningAudioRef} duration={4000} />
      <Flex className="customer base">
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          position="relative"
          gap={_isCurrentViewMobile ? "12px" : "14px"}
          bg="white"
          minH={_isCurrentViewMobile ? "115vh" : "98vh"}
          maxH={_isCurrentViewMobile ? "115vh" : "98vh"}
          borderRadius="20px"
          p={_isCurrentViewMobile ? "12px" : "22px"}
        >
          <Flex pb="0px" gap="22px" justify="space-between">
            <Tooltip label="Create new customer." fontSize="sm">
              <Button
                w="170px"
                onClick={onCustomerCreateOpen}
                leftIcon={<IoMdAdd />}
                color="rgb(92, 55, 255)"
                fontFamily="DM Sans"
                fontWeight="500"
                fontSize="16px"
                borderRadius="10px"
                className="addButton"
              >
                New Customer
              </Button>
            </Tooltip>
            <Flex gap="12px">
              <Button
                aria-label="Refresh"
                borderRadius="8px"
                fontWeight="450"
                minW={_isCurrentViewMobile ? "40px" : "20px"}
                w={_isCurrentViewMobile ? "40px" : void 0}
                px={_isCurrentViewMobile ? "0" : "16px"}
                fontSize={_isCurrentViewMobile ? "13px" : "16px"}
                backgroundColor="purple.200"
                color="purple"
                _hover={{ bg: "purple.800", color: "white" }}
                leftIcon={_isCurrentViewMobile ? void 0 : <HiArrowPath />}
                isDisabled={isRefreshing}
                onClick={_HandleRefresh}
              >
                {_isCurrentViewMobile ? <HiArrowPath /> : "Refresh"}
              </Button>
              <DownloadToExcel
                cellsData={_QueryCustomerRead?.data?.CustomerRead?.map(
                  (item, __index) =>
                    _successFlags.includes(item.status)
                      ? {
                          "S.No.": `${__index + paginationModel.pageSize * paginationModel.page + 1}.`,
                          "Company Name": _.isEmpty(item?.Company?.displayName)
                            ? "-"
                            : item?.Company?.displayName,
                          "Ref.Number": _.isEmpty(item.Company?.referenceNumber)
                            ? "-"
                            : item.Company?.referenceNumber,
                          Type: _.isEmpty(item?.type) ? "-" : item?.type,
                          "Company Email": _.isEmpty(item.Company?.email)
                            ? "-"
                            : item.Company?.email,
                          Phone: _.isEmpty(item.phone) ? "-" : item.phone,
                          Currency: _.isEmpty(item.Billing?.currency)
                            ? "-"
                            : item.Billing?.currency,
                          Balance: item.Credit?.balance || "0",
                          "Alert Amount": item.Credit?.alertAmount ?? "-",
                          "Over Draft Limit Amount":
                            item?.overDraftLimit ?? "-",
                          "Account Manager":
                            item?.AccountManager?.displayName ?? "-",
                          CreatedAt: Moment(item.createdAt).format(
                            "YYYY-MM-DD",
                          ),
                          UpdatedAt: Moment(item.updatedAt).format(
                            "YYYY-MM-DD",
                          ),
                        }
                      : {},
                )}
                headersData={[
                  "S.No.",
                  "Company Name",
                  "Ref.Number",
                  "Type",
                  "Company Email",
                  "Phone",
                  "Currency",
                  "Balance",
                  "Alert Amount",
                  "Over Draft Limit Amount",
                  "Account Manager",
                  "CreatedAt",
                  "UpdatedAt",
                ].map((i) => ({ key: i, label: i }))}
              />
            </Flex>
          </Flex>

          {/*  Filter */}
          <Flex
            gap="12px"
            flexWrap="wrap"
            bg="white"
            p="16px"
            borderRadius="12px"
            border="1px solid #C5CFE8"
            alignItems="flex-start"
          >
            <Box
              minW="150px"
              w={{ base: "100%", md: "200px" }}
              flex={{ base: "1 1 100%", md: "0 1 200px" }}
            >
              <Text fontSize="14px" fontWeight={600} color="rgb(141, 151, 181)">
                Company Name
              </Text>
              <MemoizedInput
                placeholder="Search"
                className="filter searchInput"
                defaultValue={searchQuery}
                onChange={_onSearchInputChange}
              />
            </Box>

            <Box
              minW="150px"
              w={{ base: "100%", md: "200px" }}
              flex={{ base: "1 1 100%", md: "0 1 200px" }}
            >
              <Text fontSize="14px" fontWeight={600} color="rgb(141, 151, 181)">
                Account Type
              </Text>
              <MemoizedSelect
                placeholder="Select Account Type"
                className="filter searchInput"
                onChange={_HandleCustomerTypeChange}
                options={_.without(
                  Object.React.App.enums.CUSTOMER_TYPE.enums?.map(
                    (i) => i?.key,
                  ),
                  "TERMINATION",
                )}
                data={customerType}
              />
            </Box>
          </Flex>

          {/* MUI DataGrid */}

          <DataGrid
            rows={rows}
            columns={columns}
            rowHeight={48}
            rowCount={rowCount}
            paginationMode="server"
            filterMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={_HandlePaginationModelChange}
            filterModel={filterModel}
            onFilterModelChange={_HandleFilterModelChange}
            pageSizeOptions={[10, 30, 50, 100]}
            slots={{
              loadingOverlay: () => (
                <TableSpinner
                  isLoading={true}
                  isEmpty={false}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noResultsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noRowsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) =>
              setColumnVisibilityModel(newModel)
            }
            loading={_QueryCustomerRead?.loading}
            disableColumnMenu={false}
          />
        </Flex>

        {/* Modals */}
        <Modal
          size="3xl"
          title="Customer Create"
          isOpen={isCustomerCreateOpen}
          onClose={onCustomerCreateClose}
          isCentered={false}
        >
          <CustomerUpsert isCreateOnly={true} refetch={refetch} />
        </Modal>
        <Modal
          size="lg"
          title="Block Traffic"
          isOpen={isCustomerBlockDurationOpen}
          onClose={onCustomerBlockDurationClose}
          isCentered={true}
        >
          <CustomerBlockBetweenDuration showingFor="CUSTOMER" />
        </Modal>
        <Modal
          size="3xl"
          title="Customer Update"
          isOpen={isCustomerUpdateOpen}
          onClose={onCustomerUpdateClose}
          isCentered={false}
        >
          <CustomerUpsert refetch={refetch} />
        </Modal>
        <Modal
          size="md"
          title="Attach Route and Rate"
          isOpen={isCustomerRatePlanAndRoutePlanAttachFormOpen}
          onClose={onCustomerRatePlanAndRoutePlanAttachFormClose}
        >
          <AttachRouteAndRate />
        </Modal>
        <Modal
          size="md"
          title="Customer Delete"
          isOpen={isCustomerDeleteOpen}
          onClose={onCustomerDeleteClose}
        >
          <CustomerDelete refetch={refetch} />
        </Modal>
        <Modal
          size="sm"
          title="Account Manager"
          isOpen={isCustomerAttachAccountManagerOpen}
          onClose={onCustomerAttachAccountManagerClose}
        >
          <AttachAccountManager refetch={refetch} />
        </Modal>
        <Modal
          size="sm"
          title="Customer Notify"
          isOpen={isCustomerNotifyOpen}
          onClose={onCustomerNotifyClose}
        >
          <CustomerNotify />
        </Modal>
        <Modal
          size="sm"
          title="Payment Management"
          isOpen={isCreditManagementUpsertOpen}
          onClose={onCreditManagementUpsertClose}
        >
          <CreditManagementUpsert refetch={refetch} />
        </Modal>
        <Modal
          size="sm"
          title="Customer Login Password"
          isOpen={isCustomerPasswordChangeOpen}
          onClose={onCustomerPasswordChangeClose}
        >
          <CustomerPasswordChange />
        </Modal>
        <Modal
          size="sm"
          title="Change Tps"
          isOpen={isCustomerChangeTpsOpen}
          onClose={onCustomerChangeTpsClose}
        >
          <CustomerChangeTps />
        </Modal>
      </Flex>
    </>
  );
};

/*
 * PROPTYPES
 */
Index.propTypes = {};

/*
 * EXPORTS
 */
export default Index;
